import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, FormControlLabel, RadioGroup } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import queryString, { ParsedQuery } from 'query-string';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

// Contexts
import { withNavigation, withTenant, withTranslate } from '../../core/TenantProvider';
import { getConfigProperty } from '../../core/EnvironmentConfig/EnvironmentConfig';
// Components, atoms, molecules, organisms
import APRadio from '../../components/atoms/APRadio';
import { APCheckbox } from '../../components/atoms/APInput';
import ControllerElementWrapper from '../../components/organisms/ControllerWrapper';
import APAlertMessage from '../../components/atoms/APAlertMessage';
import APBackdropLoader from '../../components/atoms/APBackdropLoader';
import BecomePartnerComplete from '../BecomePartnerContainer/BecomePartnerComplete';
import BecomePartnerFailed from '../BecomePartnerContainer/BecomePartnerFailed';
import APButton from '../../components/atoms/APButton';
import APFormMapper from '../../components/organisms/APFormMapper';
import SecurityPinScreen from '../../components/organisms/SecurityPinScreen';
import APErrorMessage from '../../components/atoms/APErrorMessage';
// Files in the same folder
import { ValidationSchema } from './MappingDefinition/validationSchema';
import NewBecomePartnerContainerStyles from './styles/NewBecomePartnerContainer.style';
import { getBecomePartnerFieldMappingDefinition } from './MappingDefinition/fieldsMappingDefinition';
//Redux
import { getServicesErrorObj } from '../../redux/modules/servicesDowntime/actions';
import { actions as DialingCodeActions } from '../../redux/modules/dialingCode'
import { actions as PartnerRegisterActions } from '../../redux/modules/partnerRegister';
import { ReducerState } from '../../redux/modules';
// Types
import { TranslateFunction } from '../../core/types/TypographyTypes';
import { BecomePartnerForm } from '../../core/types/FormTypes';
import { DialingCodeState } from '../../redux/modules/dialingCode/types';
// Utils
import { DialingCodeObj } from '../../core/utils/GetDialingCode/GetDialingCode';
import { GetDialingCodeWithoutAuth } from '../../core/utils/GetDialingCode';
import GetBasicDetails from '../../core/utils/GetBasicDetails';
import EncodeData from '../../core/utils/EncodeDecodeData';
import { ConvertToSessionCookie } from '../../core/utils/ConvertToSessionCookie';
import COUNTRIES_ALL_DATA_JSON_EN from '../../core/assets/CountriesList/countriesOptionAllData.en.json';
import { GetDialingCodeObjectAsPerCountryCode } from '../../core/utils/GetCountriesData';
import { CheckUSAOrCanadaAffiliate } from '../../core/utils/CheckUSAOrCanadaAffiliate/CheckUSAOrCanadaAffiliate';
import { CheckEUAffiliate } from '../../core/utils/checkEUAffiliate/CheckEUAffiliate';
import { TriggerGoogleAnalytics4Tag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalytics4Tags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
// Constants
import {
    BAP_ADD_TAX_COUNTRIES,
    BAP_CUSTOMER_TYPE_SWITCH_OPTIONS,
    BECOME_PARTNER_JOURNEY,
    CD,
    COUNTRY_WISE_LE_CONFIG,
    DEFAULT_LANGUAGE,
    DIALING_COUNTRY_CODE_ORG_MAPPING,
    DISABLED_COUNTRIES_IN_OPTION_LIST,
    ORG_CODE_MAPPING_FOR_LOCATION_LOOKUP,
    REFERRAL_PARTNER_COOKIE,
    MARKETING_BUSINESS_CATEGORIES,
    AFFILIATE_TERMS_URLS,
    CURRENCIES_DIRECT
} from '../../core/utils/Constants/Constants';
import { SendOtpPayload } from '../../redux/modules/partnerRegister/types';

const getCountryRegionByCode = (countryCode: string | undefined | null): string | null => {
    if (!countryCode) return null;
    const country = (COUNTRIES_ALL_DATA_JSON_EN).options.find((countryData) => countryData.value === countryCode);
    return country?.region ?? null;
};

const getAffiliateTermsUrl = (
    org: string,
    region: string | null,
    countryCode: string
): string | null => {
    if (!org) return null;

    const normalizedOrg = org === CURRENCIES_DIRECT ? 'CD' : org;
    const orgConfig = AFFILIATE_TERMS_URLS[normalizedOrg];

    if (!orgConfig) return null;

    return (
        (region && orgConfig[region]) ||
        orgConfig[countryCode] ||
        orgConfig.default ||
        null
    );
};

interface NewBecomePartnerContainerProps extends WithStyles<typeof NewBecomePartnerContainerStyles> {
    translate: TranslateFunction;
    loadingState: string;
    settings: any;
    getServicesErrorObj: Function;
    updateTranslations: Function;
    getLocationDialingCode: Function;
    sendOtPCall: Function;
    sendOtpLoadingState: string;
    otpMessage: string | null;
    forTestingCall?: boolean;
    dialingCodeData: DialingCodeState;
}

type CreatePartnerPayload = {
    clientdata: {
        client_industry: string;
        business_category: string;
        account_manager_emails: boolean;
    };
    affdetails: {
        referral_text: string;
        organisation: string;
        company: string;
        firstname: string;
        lastname: string;
        phone: string;
        country: string;
        email: string;
        password: string;
        tax_number?: string;
        referring_aff_tan_no: string | string[] | null;
        partnerflowtype: string;
    };
}


export const StyledPaper = styled(Box)({
    position: 'relative',
    marginBottom: 30,
    padding: 30,
    background: '#FFFFFF',
    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
    '& .MuiFormLabel-root': {
        '&.Mui-error': {
            color: '#f44336'
        }
    },
    '& .errorMessage': {
        width: '100%',
        margin: '0 0 32px 0',
        '& .MuiCollapse-root': {
            margin: '0 0 25px 0',
            '&.MuiCollapse-hidden': {
                margin: '0',
            }
        },
        '& .MuiAlert-action': {
            position: 'absolute',
            right: -13,
            margin: '0 !important',
            top: -8,
            padding: 0,
        },
        '& .MuiPaper-root': {
            position: 'relative',
            padding: '6px 20px',
            width: '100%',
            boxSizing: 'border-box'
        },
        '& .MuiCollapse-wrapperInner': {
            margin: 0,
            '& .MuiAlert-message': {
                padding: '13px 25px 13px 10px',
                width: '100%',
                boxSizing: 'border-box',
                display: 'block',
                margin: 0,
                '& strong': {
                    textTransform: 'uppercase'
                }
            }
        },
    }
});

const getBecomePartnerAxiosDefaults = (organisationCode: string, basicAuthToken: string) => ({
    'baseUrl': '',
    'headers': {
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Basic ${basicAuthToken}`,
        'Content-Type': 'application/json',
        'X-Organization': ORG_CODE_MAPPING_FOR_LOCATION_LOOKUP[organisationCode]
    }
});

const NewBecomePartnerContainer = (props: NewBecomePartnerContainerProps) => {
    const { classes, translate, settings, updateTranslations, sendOtPCall, sendOtpLoadingState, otpMessage, forTestingCall = false, getLocationDialingCode } = props;

    const [isAlertToBeShown, setIsAlertToBeShown] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const [refPartnerAffiliateNumber, setRefPartnerAffiliateNumber] = useState<string | string[] | null>(null);
    const [formData, setFormData] = useState<any>(null);
    const [isDuplicateEmail, setIsDuplicateEmail] = useState<boolean>(false);
    const [showOtpSection, setShowOtpSection] = useState<boolean>(false);
    const [showOtpErrorMessage, setShowOtpErrorMessage] = useState<boolean>(false);
    const [otpErrorMessageType, setOtpErrorMessageType] = useState<null | string>(null);

    const previousInteractedAnalyticsFields: React.MutableRefObject<{}> = useRef({});

    const location = useLocation();

    const { basicAuthToken: BASIC_AUTH_TOKEN, organisationCode: ORGANISATION } = GetBasicDetails();
    const dialingCodeData: DialingCodeObj = GetDialingCodeWithoutAuth();
    const defaultCountryCode: string = DIALING_COUNTRY_CODE_ORG_MAPPING[ORGANISATION ?? CD];
    const checkDuplicateEndPoint: string | null = getConfigProperty('apiEndPoint')['checkDuplicate'] || null;

    const createPartnerEndPoint = (otp) => `/affiliate-portal/registerCustomer?otpCheck=${otp}`;
    const axiosDefaults = getBecomePartnerAxiosDefaults(ORGANISATION, BASIC_AUTH_TOKEN);

    const formDefaultValues = {
        customerType: 'PFX',
        firstName: '',
        lastName: '',
        mobile: {
            input: '',
            select: dialingCodeData?.dialingCode,
            displayName: dialingCodeData?.displayName
        },
        country: defaultCountryCode,
        businessCategory: '',
        email: '',
        password: '',
        terms: false
    }

    const {
        control,
        handleSubmit,
        formState,
        watch,
        reset,
        setValue
    } = useForm<any>({
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(ValidationSchema(translate)),
        defaultValues: { ...formDefaultValues }
    });

    const { isValid, errors, touchedFields } = formState;

    const countryInput: string = watch('country');
    const emailInput: string = watch('email');
    const businessCategoryInput: string = watch('businessCategory');

    const analyticsTagsInputFields: string[] = ['firstName', 'lastName', 'mobile', 'email', 'password'];

    useEffect(() => {
        if (sendOtpLoadingState === 'LOADED' && otpMessage === 'OTP sent successfully' && formData !== null) {
            setShowOtpSection(true);
            setShowOtpErrorMessage(false);
            setOtpErrorMessageType(null);
        }
        if (sendOtpLoadingState === 'LOADED' && (otpMessage === 'Failed to send SMS/CALL invalid phone number' || otpMessage === 'Phone No format is not valid')) {
            setShowOtpErrorMessage(true);
            setOtpErrorMessageType(translate('become.partner.form.otp.send.unsuccesful.error'));
            window.scrollTo(0, 0);
        }
        if (sendOtpLoadingState === 'ERROR') {
            setShowOtpErrorMessage(true);
            setOtpErrorMessageType(translate('become.partner.form.otp.send.unsuccesful.error'));
            window.scrollTo(0, 0);
        }
        if (formData === null && showOtpSection) {
            setShowOtpSection(false);
            setShowOtpErrorMessage(false);
            setOtpErrorMessageType(null);
        }
    }, [sendOtpLoadingState, otpMessage]);

    useEffect(() => {
        // Get the latest interacted field which is a part of analyticsTagsInputFields array
        const latestInteractedField: string | null = Object.keys(touchedFields).find((key) => !(key in previousInteractedAnalyticsFields.current) && touchedFields[key] && analyticsTagsInputFields.includes(key)) || null;

        if (latestInteractedField) {
            previousInteractedAnalyticsFields.current = { ...touchedFields };

            TriggerGoogleAnalytics4Tag(
                GoogleAnalytics4Tags.registrationV2.name,
                GoogleAnalytics4Tags.eventAction.click,
                GoogleAnalytics4Tags.registrationV2.click[latestInteractedField]
            );
        }
    }, [JSON.stringify(touchedFields)]);

    useEffect(() => {
        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.registrationV2.name,
            GoogleAnalytics4Tags.eventAction.load,
            GoogleAnalytics4Tags.registrationV2.load.becomePartnerPageLoad
        );

        const { dialingCodeData } = props;
        const { RefPartner: refPartnerQueryParam }: ParsedQuery<string> = queryString.parse(location.search);
        const refPartnerCookie: string | null = ConvertToSessionCookie(REFERRAL_PARTNER_COOKIE);

        setRefPartnerAffiliateNumber(refPartnerQueryParam || refPartnerCookie || null);

        if (!forTestingCall) {
            axios.defaults.baseURL = axiosDefaults.baseUrl;
            axios.defaults.headers.get = axiosDefaults.headers;

            if (!!dialingCodeData?.locationDialingCodeData) {
                updateDialingCode(dialingCodeData?.locationDialingCodeData?.countryCode3d);
            } else {
                try {
                    getLocationDialingCode().then((res) => res?.data?.countryCode3d && updateDialingCode(res.data.countryCode3d)).catch(reset({ ...formDefaultValues }));
                } catch (error) {
                    console.error("Unexpected error:", error);
                }
            }
        }

    }, []);

    useEffect(() => {
        setValue('terms', false);

        if (BAP_ADD_TAX_COUNTRIES.includes(countryInput)) {
            setValue('taxNumber', '');
        }
        const isUSALinkURL: boolean = CheckUSAOrCanadaAffiliate(countryInput);
        const isEULinkURL: boolean = CheckEUAffiliate(countryInput);
        const translationBillCountry: string = countryInput;

        let translationLE: string | null = null;

        if (isUSALinkURL || isEULinkURL) {
            translationLE = COUNTRY_WISE_LE_CONFIG[countryInput]?.applicableLE[0];
        }
        const userSelectedLanguage: string | null = localStorage.getItem('preferredLanguage') ?? DEFAULT_LANGUAGE;

        updateTranslations(userSelectedLanguage, translationLE, translationBillCountry)
    }, [countryInput]);

    const updateDialingCode = (countryCode: string) => {
        const dialingCodeObj: DialingCodeObj = GetDialingCodeObjectAsPerCountryCode(countryCode ?? dialingCodeData);
        const { dialingCode, displayName } = dialingCodeObj || {};

        reset({
            ...formDefaultValues,
            mobile: { select: dialingCode, displayName },
            country: countryCode
        });
    };

    const createPayload = (form: BecomePartnerForm) => {
        const countryData: any[] = COUNTRIES_ALL_DATA_JSON_EN.options.filter((country) => !DISABLED_COUNTRIES_IN_OPTION_LIST.includes(country.value));

        const payLoad: CreatePartnerPayload = {
            clientdata: {
                client_industry: "",
                business_category: form.businessCategory,
                account_manager_emails: true
            },
            affdetails:
            {
                referral_text: 'internet',
                organisation: ORGANISATION,
                company: (form.customer_type === 'CFX' && form.companyName) ? form.companyName : '',
                firstname: form.firstName,
                lastname: form.lastName,
                phone: (form.mobile.select && form.mobile.input) ? `${form.mobile.select}-${form.mobile.input}` : '',
                country: (countryData) ? countryData.find(val => val.value === form.country).label.trim() : '',
                email: form.email.trim(),
                password: EncodeData(form.password),
                ...(form.taxNumber && { 'tax_number': (form.taxNumber).toUpperCase() }),
                referring_aff_tan_no: refPartnerAffiliateNumber ?? '',
                partnerflowtype: BECOME_PARTNER_JOURNEY
            }
        }

        return payLoad;
    }

    useEffect(() => {
        setIsDuplicateEmail(false);
    }, [emailInput]);


    const handleSendOtp = (formData: any) => {
        setIsError(false);
        setFormData(formData);

        const phoneNumber: string = formData.mobile.select + " " + formData.mobile.input;
        const organizationParam: string = ORGANISATION === 'CDSA' ? 'CD SA' : ORGANISATION;

        const requestPayload: SendOtpPayload = {
            phoneNumber: phoneNumber,
            organization: organizationParam
        };

        sendOtPCall(requestPayload);
    };

    const onFormSubmit = (formData: any) => {
        if (checkDuplicateEndPoint) {
            axios.defaults.baseURL = axiosDefaults.baseUrl;
            axios.defaults.headers.post = axiosDefaults.headers;
            axios
                .post(checkDuplicateEndPoint, {
                    "email_address": emailInput.trim(),
                    "brand": ORGANISATION,
                    "customer_type": 'affiliate'
                }, { timeout: 4000 }) // If after 2.5s continue
                .then((response) => {
                    if (response.data['code'] === "2009") {
                        setIsDuplicateEmail(true);
                        return;
                    }
                    setIsDuplicateEmail(false);
                    handleSendOtp(formData);
                })
                .catch((error) => {
                    console.log("error:", error);
                    setIsDuplicateEmail(false);
                    getServicesErrorObj(error);

                    handleSendOtp(formData);
                });
        }
    };

    const onOTPSubmit = (otpData) => {
        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.registrationV2.name,
            GoogleAnalytics4Tags.eventAction.click,
            GoogleAnalytics4Tags.registrationV2.click.confirmCTA
        );
        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.registrationV2.name,
            GoogleAnalytics4Tags.eventAction.select,
            GoogleAnalytics4Tags.registrationV2.select.businessCategory,
            { businessCategory: businessCategoryInput }
        );

        const otpPin: string = otpData.securityPin.join('');

        if (formData && !isError && createPartnerEndPoint) {
            const { getServicesErrorObj } = props;
            const payLoad: CreatePartnerPayload = createPayload(formData);

            setIsLoading(true);

            axios.defaults.baseURL = axiosDefaults.baseUrl;
            axios.defaults.headers.post = axiosDefaults.headers;
            axios
                .post(createPartnerEndPoint(otpPin), payLoad, { timeout: 20000 }) // If longer than 10sec
                .then(() => {
                    setShowOtpErrorMessage(false);
                    setOtpErrorMessageType(null);
                    setIsAlertToBeShown(false);
                    setIsLoading(false);
                    setIsComplete(true);
                    setIsError(false);
                })
                .catch((error) => {
                    if (error.response?.status === 504 && error.response?.data?.code === '1510') {
                        setIsAlertToBeShown(true);
                        setIsLoading(false);
                    } else if (error.response?.data === 'Invalid OTP, Please Enter Correct Code') {
                        setOtpErrorMessageType(translate('become.partner.form.otp.invalid.error'));
                        setShowOtpErrorMessage(true);
                        setIsLoading(false);
                    } else {
                        setShowOtpErrorMessage(false);
                        setOtpErrorMessageType(null);
                        getServicesErrorObj(error);
                        setIsLoading(false);
                        setIsError(true);
                    }
                });
        }
    }

    const handleBacktoLoginClick = () => {
        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.registrationV2.name,
            GoogleAnalytics4Tags.eventAction.click,
            GoogleAnalytics4Tags.registrationV2.click.backToLoginCTA
        );
    }

    const handleTermsConditionLinkClick = () => {
        if (!previousInteractedAnalyticsFields.current.hasOwnProperty('termsLink')) {
            TriggerGoogleAnalytics4Tag(
                GoogleAnalytics4Tags.registrationV2.name,
                GoogleAnalytics4Tags.eventAction.click,
                GoogleAnalytics4Tags.registrationV2.click.terms
            );

            previousInteractedAnalyticsFields.current = { ...previousInteractedAnalyticsFields.current, termsLink: true }
        }
    }

    const termsText: string = (translate('become.partner.summary.terms.label', {
        company: translate('become.partner.summary.terms.company')
    }));

    const selectedCountryRegion: string | null = getCountryRegionByCode(countryInput);
    const isMarketingBusinessCategory: boolean = MARKETING_BUSINESS_CATEGORIES.includes(businessCategoryInput);

    const shouldUseMarketingTerms: boolean = isMarketingBusinessCategory && !!businessCategoryInput && !!countryInput;
    const defaultTermsUrl: string = translate('become.partner.summary.terms.link.url');
    const termsLinkHref: string = shouldUseMarketingTerms
        ? (getAffiliateTermsUrl(ORGANISATION, selectedCountryRegion, countryInput) ?? defaultTermsUrl)
        : defaultTermsUrl;

    const termsConditionLabel: JSX.Element = (
        <>
            {termsText}
            <a className={classes.checkboxLink}
                href={termsLinkHref}
                target='_blank' rel='noopener noreferrer'
                onClick={handleTermsConditionLinkClick}>
                {translate('become.partner.summary.terms.link.text')}
            </a>
        </>
    );

    const goToBackPage = () => {
        setShowOtpSection(false)
    }

    const onErrorClose = () => {
        setShowOtpErrorMessage(false)
    }

    const isOTPApiLoading: boolean = sendOtpLoadingState === 'LOADING';

    return (
        <div className={classes.newBecomePartnerContainer}>
            {isAlertToBeShown &&
                <APAlertMessage
                    className={`serverErrorMessage`}
                    title={translate('server.timeout.error.title')}
                    message={translate('server.timeout.error.message')}
                    severity={'error'}
                    icon={'error'}
                    hideCloseIcon={false}
                    onClose={() => {
                        setIsAlertToBeShown(false);
                    }}
                />
            }
            {isLoading &&
                <APBackdropLoader open={true} />
            }

            {isComplete && (
                <BecomePartnerComplete settings={settings} />
            )}

            {isError && (
                <BecomePartnerFailed settings={settings} />
            )}

            {showOtpErrorMessage && (
                <APErrorMessage errorMessage={otpErrorMessageType} onClose={onErrorClose} />
            )}

            {!isComplete && !isError && (
                <>
                    {showOtpSection ?
                        <SecurityPinScreen
                            onOTPSubmit={onOTPSubmit}
                            goToBackPage={() => goToBackPage()}
                            formData={formData}
                        /> :
                        <>
                            <div className={classes.newBecomePartnerContainerTitle}>
                                <h1>{translate('become.partner.heading')}</h1>
                                <p>{translate('become.partner.paragraph')}</p>
                            </div>
                            <div className={classes.newBecomePartnerFormSteps}>
                                <form onSubmit={handleSubmit(onFormSubmit)}>
                                    <StyledPaper>
                                        <ControllerElementWrapper
                                            className={classes.formSectionRadio}
                                            name='customerType'
                                            component={RadioGroup}
                                            control={control}
                                        >
                                            {BAP_CUSTOMER_TYPE_SWITCH_OPTIONS.map((option: any) => (
                                                <FormControlLabel
                                                    value={option.value}
                                                    control={<APRadio />}
                                                    label={translate(`${option.key}.label`)}
                                                />
                                            ))}
                                        </ControllerElementWrapper>
                                        <APFormMapper
                                            control={control}
                                            errors={errors}
                                            fields={getBecomePartnerFieldMappingDefinition({ translate, classes, isDuplicateEmail })}
                                            watch={watch}
                                            translate={translate}
                                        />
                                    </StyledPaper>
                                    <Box className={classes.formTermsCondition}>
                                        <ControllerElementWrapper
                                            component={APCheckbox}
                                            label={termsConditionLabel}
                                            control={control}
                                            name='terms'
                                        />
                                    </Box>
                                    <div className={classes.stepButtons}>
                                        <div className={classes.stepButtonsLeft}>
                                            <APButton
                                                children={translate('step.container.form.button.backtologin')}
                                                className={classes.stepButton}
                                                color='primary'
                                                variant='outlined'
                                                href='/login'
                                                onClick={handleBacktoLoginClick}
                                            />
                                        </div>
                                        <div className={classes.stepButtonsRight}>
                                            <APButton
                                                children={translate('step.container.form.button.confirm')}
                                                className={classes.stepButton}
                                                color='primary'
                                                disabled={!isValid || isDuplicateEmail || isOTPApiLoading}
                                                variant='contained'
                                                type='submit'
                                                startIcon={isOTPApiLoading ? <CircularProgress size={20} className={classes.loaderSpinner} /> : null}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </>
                    }
                </>
            )}
        </div>
    )
};

const mapStateToProps = (store: ReducerState) => ({
    sendOtpLoadingState: store.partnerRegister.sendOtpLoadingState,
    otpMessage: store.partnerRegister.otpMessage,
    dialingCodeData: store.dialingCode
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getServicesErrorObj: getServicesErrorObj,
    sendOtPCall: PartnerRegisterActions.sendOtPCall,
    getLocationDialingCode: DialingCodeActions.getLocationDialingCode
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withNavigation(
        withTenant(
            withTranslate(
                withStyles(NewBecomePartnerContainerStyles)(
                    NewBecomePartnerContainer
                )
            )
        )
    )
);